// Risk-score methodology explainer (v2). Ported from the v2 spec
// (risk-methodology-v2): 2-stage stage-depth, no sensitivity multiplier and
// low-activity blending toward neutral. No smoothing across days (BD-1245): the
// score is each day's result, rounded to a whole number.
// The org score is the plain mean of active user scores (the v2 volume floor
// was removed 2026-08). Interactive demos re-themed to the reports-b look.
//
// Static content — the constants below are the v2 source of truth and are kept
// in sync with the server's v2 computation (DashboardRepository / refresh).
import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'motion/react';
import MainCard from '../../../components/cards/MainCard';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

const SIGNALS_V2 = [
    { label: 'Failure rate', weight: 35, def: 'Failed tests divided by total tests delivered in the rolling 6-month window. Simulation-type agnostic.' },
    { label: 'Repeat offender', weight: 20, def: 'Clicks that follow straight on from another click, divided by total tests in the window, ordered by send date. In a run of consecutive clicks the first does not count and each one after it counts once, so two in a row score 1 and four in a row score 3. Isolated clicks score nothing.' },
    { label: 'Stage depth', weight: 20, def: 'How far the user got on their worst failed test, across two stages: first stage, the user clicked the link in the email (50%); second stage, the user continued through to the landing page (100%). Landing pages are not all credential-capture pages. We record the stage reached, not what was entered.' },
    { label: 'Training debt', weight: 15, def: 'Overdue courses divided by total courses assigned in the window. Reminders are not counted.' },
    { label: 'Course lag', weight: 10, def: 'Average days each course assigned in the window took to complete, or has been open so far, capped at 30 per course. Higher means slower.' },
];

const BANDS_V2 = [
    { name: 'Low', min: 0, max: 29, meaning: 'Consistently safe behaviour across the window.' },
    { name: 'Medium', min: 30, max: 55, meaning: 'Some risk signals present; worth keeping an eye on.' },
    { name: 'High', min: 56, max: 75, meaning: 'Repeated or more serious risk behaviour.' },
    { name: 'Critical', min: 76, max: 100, meaning: 'Urgent. High-risk behaviour across several signals.' },
];

const BAND_COLOR = { Low: '#22c55e', Medium: '#f59e0b', High: '#fb7185', Critical: '#ef4444' };
const BAND_BG = { Low: 'rgba(34,197,94,.15)', Medium: 'rgba(245,158,11,.16)', High: 'rgba(251,113,133,.16)', Critical: 'rgba(239,68,68,.16)' };
const SIG_COLORS = ['#a78bfa', '#5eead4', '#93c5fd', '#fcd34d', '#cbd5e1'];
const WEIGHTS = SIGNALS_V2.map((s) => s.weight);

function bandOf(s) { return s < 30 ? 'Low' : s < 56 ? 'Medium' : s < 76 ? 'High' : 'Critical'; }

// --------------------------------------------------------------------------
//  Presentational helpers
// --------------------------------------------------------------------------
function Section({ n, title, children }) {
    return (
        <motion.div variants={fadeUp}>
            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                <Box sx={{ p: 3.5 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1 }}>
                        <Box sx={{ flexShrink: 0, width: 26, height: 26, borderRadius: '50%', bgcolor: 'secondary.main', color: 'common.white', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n}</Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
                    </Box>
                    {children}
                </Box>
            </MainCard>
        </motion.div>
    );
}

function Lead({ children }) {
    return <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.65, mb: 1.5, maxWidth: 780 }}>{children}</Typography>;
}

function Code({ children }) {
    return (
        <Box sx={{ p: 2, my: 1.5, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: '10px', fontFamily: 'ui-monospace, "SF Mono", "Cascadia Code", monospace', fontSize: 12.5, color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {children}
        </Box>
    );
}

function BandChip({ name, small }) {
    return (
        <Box sx={{ display: 'inline-flex', px: small ? '9px' : '11px', py: small ? '2px' : '4px', borderRadius: '999px', fontSize: small ? 10.5 : 11.5, fontWeight: 700, bgcolor: BAND_BG[name], color: BAND_COLOR[name] }}>{name}</Box>
    );
}

function Callout({ title, children }) {
    return (
        <Box sx={{ display: 'flex', gap: 1.25, mt: 1, p: 1.75, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: '10px' }}>
            <Box sx={{ flexShrink: 0, mt: '2px', width: 16, height: 16, borderRadius: '50%', border: '1.5px solid', borderColor: 'secondary.main', color: 'secondary.main', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>i</Box>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.6 }}>
                {title && <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>{title} </Box>}{children}
            </Typography>
        </Box>
    );
}

function DemoShell({ heading, note, children }) {
    return (
        <Box sx={{ mt: 2, p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: '12px', bgcolor: 'action.hover' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'text.disabled', mb: note ? 0.5 : 2 }}>{heading}</Typography>
            {note && <Typography sx={{ fontSize: 12.5, color: 'text.disabled', mb: 2, maxWidth: 660, lineHeight: 1.5 }}>{note}</Typography>}
            {children}
        </Box>
    );
}

function Slider({ label, value, min, max, step, onChange, display, theme }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, my: 1 }}>
            <Typography sx={{ flexShrink: 0, width: 132, fontSize: 12.5, color: 'text.secondary' }}>{label}</Typography>
            <input type="range" min={min} max={max} step={step || 1} value={value} onChange={(e) => onChange(Number(e.target.value))}
                style={{ flex: 1, height: 5, accentColor: theme.palette.secondary.main, cursor: 'pointer' }} />
            <Typography sx={{ flexShrink: 0, width: 58, textAlign: 'right', fontFamily: 'ui-monospace, monospace', fontSize: 12.5, fontWeight: 600, color: 'text.primary' }}>{display}</Typography>
        </Box>
    );
}

// --------------------------------------------------------------------------
//  §2 interactive: build one user's score
// --------------------------------------------------------------------------
function UserScoreBuilder({ theme }) {
    const [vals, setVals] = useState([40, 30, 55, 20, 25]);
    const contrib = vals.map((v, i) => WEIGHTS[i] * v / 100);
    const score = contrib.reduce((a, b) => a + b, 0);
    const shown = Math.round(score); // shown as a whole number and banded on that, as on the server
    const band = bandOf(shown);
    const set = (i, v) => setVals((p) => p.map((x, j) => (j === i ? v : x)));
    const ticks = [0, 30, 56, 76, 100];
    const zones = [[15, 'Low'], [43, 'Medium'], [66, 'High'], [88, 'Critical']];
    return (
        <DemoShell heading="Build one user's score: move any signal and watch it add up">
            {SIGNALS_V2.map((s, i) => (
                <Slider key={s.label} label={s.label} value={vals[i]} min={0} max={100} onChange={(v) => set(i, v)} display={`${vals[i]}%`} theme={theme} />
            ))}
            <Box sx={{ mt: 2.5, mb: 0.5 }}>
                <Box sx={{ position: 'relative', height: 16, mb: '3px' }}>
                    {ticks.map((v) => (
                        <Typography key={v} sx={{ position: 'absolute', left: `${v}%`, transform: 'translateX(-50%)', fontFamily: 'ui-monospace, monospace', fontSize: 9.5, color: 'text.disabled' }}>{v}</Typography>
                    ))}
                </Box>
                <Box sx={{ display: 'flex', height: 34, borderRadius: '9px', overflow: 'hidden', border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                    {contrib.map((c, i) => <Box key={i} sx={{ width: `${c}%`, height: '100%', bgcolor: SIG_COLORS[i] }} />)}
                    <Box sx={{ flex: 1, bgcolor: 'background.default' }} />
                </Box>
                <Box sx={{ position: 'relative', height: 14, mt: '4px' }}>
                    {zones.map(([left, name]) => (
                        <Typography key={name} sx={{ position: 'absolute', left: `${left}%`, transform: 'translateX(-50%)', fontFamily: 'ui-monospace, monospace', fontSize: 9.5, color: 'text.disabled' }}>{name}</Typography>
                    ))}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mt: 2.5 }}>
                <Typography sx={{ fontSize: 30, fontWeight: 700, color: 'text.primary' }}>{shown}</Typography>
                <BandChip name={band} />
            </Box>
            <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, color: 'text.disabled', mt: 1.5 }}>
                Score = {WEIGHTS.map((w, i) => `${w}×${(vals[i] / 100).toFixed(2)}`).join(' + ')} = {score.toFixed(1)}, rounded to <Box component="span" sx={{ color: 'text.secondary', fontWeight: 700 }}>{shown}</Box>
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1.75 }}>
                {SIGNALS_V2.map((s, i) => (
                    <Box key={s.label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 11.5, color: 'text.disabled' }}>
                        <Box sx={{ width: 11, height: 11, borderRadius: '3px', bgcolor: SIG_COLORS[i] }} />{s.label}
                    </Box>
                ))}
            </Box>
        </DemoShell>
    );
}

// --------------------------------------------------------------------------
export default function RiskMethodologyPage() {
    const theme = useTheme();
    const th = ({ px = 2, py = 1.25 } = {}) => ({ px, py, fontSize: 11, fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid', borderColor: 'divider' });
    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '6px 24px 48px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {/* Hero */}
            <motion.div variants={fadeUp}>
                <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: '14px', border: '1px solid', borderColor: 'divider', background: 'linear-gradient(135deg, rgba(0,191,165,.10) 0%, rgba(0,191,165,.02) 55%, transparent 100%)', p: { xs: 2.5, md: 3.5 }, display: 'flex', alignItems: 'center', gap: 2.5 }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: '14px', bgcolor: 'rgba(0,191,165,.20)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 0 0 1px rgba(0,191,165,.30), 0 6px 16px rgba(0,191,165,.20)' }}>
                        <ShieldOutlinedIcon sx={{ fontSize: 28, color: 'secondary.main' }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Risk score {'-'} Methodology</Typography>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em', mt: 0.25 }}>How your score is built, step by step</Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.55, mt: 0.5, maxWidth: 660 }}>Five behavioural signals per user, summed into a 0{'-'}100 score, then averaged across the org. Lower is better.</Typography>
                    </Box>
                </Box>
            </motion.div>

            {/* 1. Signals */}
            <Section n={1} title="Signals">
                <Lead>Five behavioural signals are sampled per user across the last 6 months. Each is normalised to a 0{'–'}1 value and weighted as follows.</Lead>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', overflow: 'hidden' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '160px 70px 1fr', bgcolor: 'background.paper' }}>
                        {['Signal', 'Weight', 'Definition'].map((h) => <Box key={h} sx={th()}>{h}</Box>)}
                    </Box>
                    {SIGNALS_V2.map((s, i) => (
                        <Box key={s.label} sx={{ display: 'grid', gridTemplateColumns: '160px 70px 1fr', alignItems: 'start', borderTop: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ px: 2, py: 1.5, fontSize: 12.5, fontWeight: 600, color: 'text.primary' }}>{s.label}</Box>
                            <Box sx={{ px: 2, py: 1.5, fontSize: 12.5, fontWeight: 700, color: 'secondary.main', fontFamily: 'ui-monospace, monospace' }}>{s.weight}%</Box>
                            <Box sx={{ px: 2, py: 1.5, fontSize: 11.5, color: 'text.secondary', lineHeight: 1.55 }}>{s.def}</Box>
                        </Box>
                    ))}
                </Box>
                <Callout title="Low-activity users.">When there is little to go on, for example only a couple of tests in the window, each signal is blended toward a neutral baseline, so a single event can{'’'}t swing the whole score. The more activity a user has, the more their score reflects their actual behaviour rather than the baseline. New users and brand-new tenants therefore start near neutral and earn their score as evidence builds.</Callout>
            </Section>

            {/* 2. User score */}
            <Section n={2} title="User score">
                <Lead>The five components are multiplied by their weights and summed, then bounded to the 0{'–'}100 range. Nothing else is applied on top. The score is exactly the sum of its parts, which means any score reconciles straight back to the underlying signals.</Lead>
                <Code>{`Score = round(clamp(
    (35 × FailRate) + (20 × RepeatOffender) + (20 × StageDepth)
    + (15 × TrainingDebt) + (10 × CourseLag),
  0, 100))`}</Code>
                <Lead>Because each signal sits between 0 and 1, the score always lands between 0 and 100. Higher always means higher risk: improving any signal can only lower the score, never raise it.</Lead>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', overflow: 'hidden' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '120px 90px 1fr', bgcolor: 'background.paper' }}>
                        {['Band', 'Range', 'What it means'].map((h) => <Box key={h} sx={th()}>{h}</Box>)}
                    </Box>
                    {BANDS_V2.map((b) => (
                        <Box key={b.name} sx={{ display: 'grid', gridTemplateColumns: '120px 90px 1fr', alignItems: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ px: 2, py: 1.25 }}><BandChip name={b.name} small /></Box>
                            <Box sx={{ px: 2, py: 1.25, fontSize: 12, fontWeight: 700, color: 'secondary.main', fontFamily: 'ui-monospace, monospace' }}>{b.min}{'–'}{b.max}</Box>
                            <Box sx={{ px: 2, py: 1.25, fontSize: 12, color: 'text.secondary' }}>{b.meaning}</Box>
                        </Box>
                    ))}
                </Box>
                <UserScoreBuilder theme={theme} />
                <Lead><Box component="span" sx={{ mt: 2.5, display: 'block' }}>Your score is worked out afresh every day from your last 6 months of activity and shown as a whole number. It is not averaged with earlier days, so it moves as soon as a new result comes in or an old one leaves the 6-month window.</Box></Lead>
            </Section>

            {/* 3. Org score */}
            <Section n={3} title="Organisation score">
                <Lead>The org score is the average of all active user scores (active = at least one simulation in the 6-month window). A simple average is used so the org number always reconciles back to the user list: improve any user{'’'}s score and the org score moves with it.</Lead>
                <Code>{`Org score = round(mean(active user scores))`}</Code>
                <Lead>Users with no simulation in the window are not scored, even if they have courses, and do not count toward the average, so a large pool of dormant accounts can neither mask nor inflate the organisation{'’'}s risk.</Lead>
            </Section>
        </motion.div>
    );
}
